import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import { setGA4Event } from "@tggstore/Handlers/Utilities/CustomTracking";
import { CategorySchemas } from "@tggstore/Types/CustomModels";
import { removeMetadata } from "@tggstore/Utilities/Metadata";
import * as React from "react";
import { ReactNode, useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

// verndale code
const mapStateToProps = (state: ApplicationState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    const session = state.context.session;

    return {
        currentPage: getCurrentPage(state),
        userIsAuthenticated,
        session,
    };
};
// end verndale code

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps>;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
    // Verndale Customization
    currentPage,
    userIsAuthenticated,
    session,
}: // End Verndale Customization

Props) => {
    const [layoutMounted, setLayoutMounted] = React.useState(false);
    // Verndale Customization: Remove metadata when the page changes
    useEffect(() => {
        const handleRemoveMetadata = () => {
            if (currentPage.type === "NullPage") {
                return;
            }

            if (currentPage.type !== "CategoryDetailsPage" && currentPage.type !== "HomePage") {
                removeMetadata(CategorySchemas.categoryListItemList);
                removeMetadata(CategorySchemas.categoryListBreadCrumbList);
                removeMetadata(CategorySchemas.categoryDetailOrganization);
            }

            if (currentPage.type === "HomePage") {
                removeMetadata("categoryListBreadCrumbList");
                removeMetadata("productList_BreadCrumbListSchema");
            }
        };

        handleRemoveMetadata();

        return () => {
            handleRemoveMetadata();
        };
    }, [currentPage]);

    useEffect(() => {
        if (currentPage.type === "NullPage") {
            return;
        }

        if (userIsAuthenticated && session) {
            setGA4Event([], "none", {
                user_id: session.userProfileId || "",
            });
        }
    }, [session, currentPage, userIsAuthenticated]);

    useEffect(() => {
        console.log("PageLayout mounted");

        // Check for pending GA4 event
        const pendingSignIn = sessionStorage.getItem("pendingSignInGA4");
        const signInUserId = sessionStorage.getItem("signInUserId");

        if (pendingSignIn === "true") {
            // Create an observer instance
            const observer = new MutationObserver((mutations, obs) => {
                // Look for a warehouse-menu-label element that indicates page is ready
                const pageIsReady = document.querySelector(".warehouse-menu-label");

                if (pageIsReady) {
                    console.log("Page is fully loaded, setting GA4 event");
                    setGA4Event([], "login", {
                        user_id: signInUserId || "",
                    });
                    sessionStorage.setItem("pendingSignInGA4", "false");
                    sessionStorage.setItem("signInUserId", "");

                    // Disconnect the observer
                    obs.disconnect();
                }
            });

            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            // Cleanup
            return () => observer.disconnect();
        }
    }, []);

    return (
        <PageLayoutWrapper>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && (
                <Page as="div">
                    <PageBreadcrumbs />
                </Page>
            )}
            {pageContent}
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default connect(mapStateToProps)(PageLayout as any);
